<!-- src/components/KpiChart.vue -->
<template>
  <div>
    <p class="text-h6">{{ title }}
      <span v-if="id === 'KpiSales'">
        <v-tooltip
          v-model="showSalesInfo"
          top
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              v-bind="attrs"
              v-on="on"
              class="no-hover"
              small
            >
              <v-icon color="orange" size="20">
                mdi-information
              </v-icon>
            </v-btn>
          </template>
          <span> {{ $t('chartsSalesInfoTooltip') }}</span>
        </v-tooltip>
      </span>
    </p>
    <p v-if="id === 'KpiSales'" class="text-h5 font-weight-bold mb-0">{{ $t('total') }}: {{ selectedCurrency.attributes.code }} {{ total.toLocaleString('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 0, useGrouping: true, style: 'decimal' }).replace(',', '.') }}</p>
    <p v-else class="text-h5 font-weight-bold mb-0">{{ $t('total') }}: {{ total.toLocaleString('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 0, useGrouping: true, style: 'decimal' }).replace(',', '.') }}</p>
    <div class="d-flex flex-row align-center" style="height: 46px">
      <v-spacer /> 
      <v-select
        v-if="id === 'KpiSales'"
        v-model="selectedCurrency"
        :items="currencies"
        hide-details
        solo
        item-text="attributes.code"
        item-value="attributes"
        return-object
        class="currency-select mr-2"
        :menu-props="{
          width: '50px',
          minWidth: '50px',
        }"
        data-testid="currency-select"
        style="max-width: 62px;"
        dense
      />
      <DateFilterCharts @dateFilter="dateFilter" />
    </div>
    <apexchart
    ref="chart"
      type="line"
      :options="chartOptions"
      :series="chartSeries"
      width="100%"
      height="220px"
    ></apexchart>
  </div>
</template>
  
  <script>
import VueApexCharts from "vue-apexcharts";
import DateFilterCharts from "@/components/DateFilterCharts.vue";
import moment from "moment";
import { getCurrencies } from "@/api/productApi";
import i18n from "../plugins/i18n";
import se from "apexcharts/dist/locales/se.json";
import en from "apexcharts/dist/locales/en.json";

export default {
  components: {
    apexchart: VueApexCharts,
    DateFilterCharts,
  },
  props: {
    title: String,
    total: Number,
    textLeft: String,
    downloadedFileTitle: String,
    data: Array,
    id: String,
  },
  data() {
    return {
      language: window.$cookies.get('language') ? window.$cookies.get('language') : 'en',
      startDate: moment().startOf("year").format("YYYY-MM-DD"),
      endDate: moment().format("YYYY-MM-DD"),
      chartOptions: {...this.chartOptions, ...{
          chart: {
              id: this.id,
              defaultLocale: 'en',
              locales: [en, se, {
                  name: 'mk',
                  options: {
                      months: ['Јануари', 'Февруари', 'Март', 'Април', 'Мај', 'Јуни', 'Јули', 'Август', 'Септември', 'Октомври', 'Ноември', 'Декември'],
                      shortMonths: ['Јан', 'Фев', 'Мар', 'Апр', 'Мај', 'Јун', 'Јул', 'Авг', 'Сеп', 'Окт', 'Ное', 'Дек'],
                      days: ['Недела', 'Понеделник', 'Вторник', 'Среда', 'Четврток', 'Петок', 'Сабота'],
                      shortDays: ['Нед', 'Пон', 'Вто', 'Сре', 'Чет', 'Пет', 'Саб'],
                      toolbar: {
                          exportToSVG: "Преземи SVG",
                          exportToPNG: "Преземи PNG",
                          exportToCSV: "Преземи CSV",
                          menu: "Мени",
                          selection: "Избор",
                          selectionZoom: "Изборно зголемување",
                          zoomIn: "Зголемување приказ",
                          zoomOut: "Намалување приказ",
                          pan: "Движење",
                          reset: "Ресетирај приказ"
                      }
                  }
              }],
              toolbar: {
                export : {
                  csv : {
                    filename: this.downloadedFileTitle,
                    headerCategory: i18n.t('labelDate'),
                  },
                  svg: {
                    filename: this.downloadedFileTitle
                  },
                  png: {
                    filename: this.downloadedFileTitle
                  }
                }
              }
          },
          colors: this.id == "KpiProducts" ? ['#9600CD'] : this.id == "KpiParticipants" ? ['#2962FF'] : this.id == "KpiOrders" ? ['#3519EC'] : ['#E400E0'],
          xaxis: {
              type: 'datetime',
              title: {
                  text: i18n.t('labelDate')
              },
              //categories: ['2024-07-01', '2024-07-03', '2024-07-05']
              //categories: ['1/07/2024', '4/07/2024', '5/07/2024'],
              categories: this.data.map(item => moment(item.date).format('YYYY-MM-D'))
              // labels: {
              //   datetimeFormatter: {
              //     year: 'yyyy',
              //     month: "MMM 'yy",
              //     day: 'dd MMM',
              //   },
              // }
          },
          yaxis: {
              title: {
                text: this.textLeft
              },
              labels: {
                formatter: (value) => value.toLocaleString('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 0, useGrouping: true, style: 'decimal' }).replace(',', '.'),
              },
              min: this.id == "KpiSales" ? undefined : 0 //issue with showing value 1 inside chart
          },
          title: {
              text: '',
              align: 'left'
          },
          stroke: {
              //curve: 'straight' //smooth, straight, stepline, natural
              curve: 'smooth',
          },
          markers: {
              size: 4,
              hover: {
                  sizeOffset: 2
              }
          }},
      },
      chartSeries: [
        {
          name: this.title,
          data: [],
        },
      ],
      selectedCurrency: {id:1, attributes: {name: 'Swedish Krona', code: 'SEK', symbol: 'kr'}},
      currencies: [],
      showSalesInfo: false
    };
  },
  methods: {
    updateChart(created = false) {
      this.$emit("getNewChartData", this.id, created, this.startDate, this.endDate, this.selectedCurrency.attributes.code);
    },
    updateChartSeries() {
      this.chartSeries = [
        {
          name: this.title,
          data: this.data.map((item) => ({
            x: new Date(item.date),
            y: item.value,
          })),
        },
      ];
    },
    dateFilter(startDate, endDate) {
      this.startDate = startDate;
      this.endDate = endDate;
    },
    async fetchCurrencies(){
        try {
            const response = await getCurrencies();
            if(response.status == 200) {
                this.currencies = response.data.data;
            }                
        } catch (e) {
            if (e.response) {
                this.alertValue = true;
                this.alertType = "error";
                this.alertMsg = i18n.t("serverError");
            }
        }
    },
  },

  watch: {
    startDate() {
      this.updateChart(false);
    },
    endDate() {
      this.updateChart(false);
    },
    selectedCurrency() {
      this.updateChart(false);
    },
    data() {
      this.updateChartSeries();
    },
  },

  mounted() {
    if(this.language === 'sv'){
      this.$refs.chart.chart.setLocale('se') 
    } else if(this.language === 'mk'){
      this.$refs.chart.chart.setLocale('mk')
    } else {
      this.$refs.chart.chart.setLocale('en')
    }
  },

  async created() {
    this.updateChart(true);
    if(this.id == "KpiSales"){
      await this.fetchCurrencies();
    }
  },
};
</script>